import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CLINIC } from "@/data/clinic";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";

const POLICIES = {
    "privacy-policy": {
        title: "Privacy Policy",
        updated: "Last updated: July 2026",
        sections: [
            {
                h: "Who we are",
                p: `This website is operated by ${CLINIC.name}, located at ${CLINIC.addressLines.join(" ")} You can reach us on ${CLINIC.phoneDisplay}.`,
            },
            {
                h: "What we collect",
                p: "This website does not contain contact forms or account registration. If you contact us by phone or WhatsApp, we receive only the information you choose to share — such as your name, phone number and the reason for your enquiry. This website may use privacy-respecting analytics to understand overall visitor patterns.",
            },
            {
                h: "How we use it",
                p: "Information you share with us is used only to respond to your enquiry, arrange consultations and provide our wellness services. We do not sell, rent or trade your personal information to anyone.",
            },
            {
                h: "Third-party services",
                p: "Links to Google Maps and WhatsApp take you to services operated by their respective providers, whose own privacy policies apply once you leave this website.",
            },
            {
                h: "Your choices",
                p: "You may ask us at any time to update or delete the information you have shared with us by calling the clinic.",
            },
        ],
    },
    "terms-and-conditions": {
        title: "Terms & Conditions",
        updated: "Last updated: July 2026",
        sections: [
            {
                h: "About this website",
                p: `This website provides information about ${CLINIC.name}, a wellness centre in Kurnool, Andhra Pradesh, and the traditional and complementary approaches offered there.`,
            },
            {
                h: "Nature of services",
                p: "Our services are complementary wellness practices inspired by traditional systems such as Ayurveda. They are not medical services, and nothing on this website constitutes medical advice, diagnosis or treatment. Please read our Medical Disclaimer.",
            },
            {
                h: "Appointments",
                p: "Consultations are arranged by phone or WhatsApp. We request reasonable notice if you need to reschedule so the time can be offered to another guest.",
            },
            {
                h: "Accuracy of information",
                p: "We take care to keep this website accurate and to describe traditional practices responsibly. Educational content about Marma Chikitsa reflects classical tradition and is not a promise of any outcome.",
            },
            {
                h: "Intellectual property",
                p: "The text, design and branding of this website belong to the clinic and may not be reproduced without permission.",
            },
            {
                h: "Governing law",
                p: "These terms are governed by the laws of India, with jurisdiction in Kurnool, Andhra Pradesh.",
            },
        ],
    },
    "medical-disclaimer": {
        title: "Medical Disclaimer",
        updated: "Last updated: July 2026",
        sections: [
            {
                h: "Complementary wellness, not medical care",
                p: "Marma Chikitsa and the other approaches offered at our centre are complementary wellness practices drawn from classical traditions. They are not intended to diagnose, treat, cure or prevent any disease, and we make no such claims.",
            },
            {
                h: "Traditional use vs. modern evidence",
                p: "Descriptions on this website of marma points and traditional benefits reflect classical Ayurvedic literature and long-standing traditional use. Modern scientific research on many of these practices is still developing, and traditional use should not be read as proven medical effect.",
            },
            {
                h: "Consult your physician",
                p: "Always seek the advice of a qualified medical practitioner for any health concern, and never disregard or delay medical care because of anything you read on this website. Continue any prescribed treatment unless your doctor advises otherwise. If you are pregnant, recovering from surgery or injury, or managing a medical condition, please consult your physician before booking a session and inform us beforehand.",
            },
            {
                h: "Emergencies",
                p: "This website and our services are not for medical emergencies. If you are experiencing a medical emergency, please contact emergency services or visit the nearest hospital immediately.",
            },
        ],
    },
};

export default function PolicyPage({ slug }) {
    const policy = POLICIES[slug];
    return (
        <div className="min-h-screen bg-cream text-ink" data-testid={`policy-page-${slug}`}>
            <Seo
                title={`${policy.title} | ${CLINIC.name}`}
                description={`${policy.title} of ${CLINIC.name}, a traditional wellness centre in Kurnool, Andhra Pradesh.`}
                path={`/${slug}`}
            />
            <header className="border-b border-sand/70 bg-cream/90 backdrop-blur-xl">
                <div className="mx-auto flex h-[72px] max-w-4xl items-center justify-between px-4 sm:px-6">
                    <Link to="/" aria-label="Back to home">
                        <Logo />
                    </Link>
                    <Link
                        to="/"
                        data-testid="policy-back-btn"
                        className="inline-flex items-center gap-2 rounded-full border border-forest px-4 py-2 text-xs font-semibold text-forest transition-colors duration-300 hover:bg-forest hover:text-cream"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
                    </Link>
                </div>
            </header>
            <main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 md:py-24">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">{policy.updated}</p>
                <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight md:text-5xl" data-testid="policy-title">
                    {policy.title}
                </h1>
                <div className="mt-12 space-y-10">
                    {policy.sections.map((s, i) => (
                        <section key={i}>
                            <h2 className="font-serif text-2xl font-medium text-forest">{s.h}</h2>
                            <p className="mt-3 text-sm leading-relaxed text-moss md:text-base">{s.p}</p>
                        </section>
                    ))}
                </div>
                <p className="mt-16 border-t border-sand pt-8 text-xs text-moss/70">
                    {CLINIC.name} — {CLINIC.addressLines.join(" ")} Phone: {CLINIC.phoneDisplay}
                </p>
            </main>
        </div>
    );
}
